import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  birth_year: "",
  household_size: "",
  pets: "",
  smoker: false,
  occupation: "",
  employer: "",
  income_range: "",
  schufa_ready: false,
  preferred_city: "",
  preferred_property: "",
  rooms: "",
  move_in: "",
  message: "",
  consent: false,
};

export default function Bewerbung() {
  const [params] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    ...initial,
    preferred_property: params.get("objekt") || "",
  });

  useEffect(() => {
    axios.get(`${API}/properties`).then((r) => setProperties(r.data || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Bitte stimmen Sie den Datenschutzbedingungen zu.");
    const required = ["name", "email", "household_size", "pets", "occupation", "income_range"];
    for (const k of required) {
      if (!form[k]) return toast.error("Bitte füllen Sie alle Pflichtfelder aus.");
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/rental-application`, form);
      setDone(true);
      toast.success("Vielen Dank — Ihre Bewerbung ist bei uns eingegangen.");
    } catch {
      toast.error("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="page-bewerbung-success" className="px-6 md:px-12 lg:px-24 py-32 min-h-[70vh] flex items-center">
        <div className="max-w-2xl mx-auto text-center">
          <CheckCircle2 className="h-14 w-14 text-primary mx-auto mb-8" strokeWidth={1.5} />
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4">Bewerbung gesendet</div>
          <h1 className="font-serif text-4xl sm:text-5xl font-light leading-tight">
            Vielen Dank für Ihre Bewerbung.
          </h1>
          <p className="mt-6 text-base text-muted-foreground leading-relaxed">
            Wir melden uns persönlich bei Ihnen, sobald ein passendes Objekt frei
            wird oder wir Rückfragen haben.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-bewerbung">
      <section className="px-6 md:px-12 lg:px-24 pt-32 md:pt-40 pb-12">
        <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-primary mb-6">
          <span className="inline-block w-2 h-2 bg-accent" />
          Mietbewerbung
        </div>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.05] max-w-4xl">
          Bewerben Sie sich auf eine <em className="italic">Wohnung</em>.
        </h1>
        <p className="mt-8 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">
          Wir hinterlegen Ihre Bewerbung in unserer Interessentenliste und
          melden uns persönlich, sobald ein passendes Objekt verfügbar wird.
          Pflichtfelder sind mit <span className="text-primary">*</span> markiert.
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-24 pb-32">
        <form onSubmit={submit} className="max-w-2xl space-y-10" data-testid="bewerbung-form">
          {/* Section: Person */}
          <Group title="Zur Person">
            <Field label="Ihr Name *">
              <UnderlineInput data-testid="bewerbung-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Vor- und Nachname" required />
            </Field>
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="E-Mail *">
                <UnderlineInput data-testid="bewerbung-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="ihre@email.de" required />
              </Field>
              <Field label="Telefon">
                <UnderlineInput data-testid="bewerbung-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="optional" />
              </Field>
            </div>
            <Field label="Geburtsjahr">
              <UnderlineInput data-testid="bewerbung-birth-year" value={form.birth_year} onChange={(e) => set("birth_year", e.target.value)} placeholder="z. B. 1985" />
            </Field>
          </Group>

          {/* Section: Haushalt */}
          <Group title="Zum Haushalt">
            <Field label="Anzahl Personen im Haushalt *">
              <UnderlineSelect testId="bewerbung-household" value={form.household_size} onChange={(v) => set("household_size", v)} placeholder="Bitte wählen"
                options={[
                  ["1", "1 Person"],
                  ["2", "2 Personen"],
                  ["3", "3 Personen"],
                  ["4", "4 Personen"],
                  ["5+", "5 oder mehr"],
                ]}
              />
            </Field>
            <Field label="Haustiere *">
              <UnderlineSelect testId="bewerbung-pets" value={form.pets} onChange={(v) => set("pets", v)} placeholder="Bitte wählen"
                options={[
                  ["keine", "Keine Haustiere"],
                  ["katze", "Katze(n)"],
                  ["hund", "Hund(e)"],
                  ["andere", "Andere Tiere"],
                ]}
              />
            </Field>
            <div className="flex items-center gap-3 pt-1">
              <Checkbox
                id="smoker"
                data-testid="bewerbung-smoker"
                checked={form.smoker}
                onCheckedChange={(v) => set("smoker", !!v)}
                className="rounded-none border-foreground/40 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <Label htmlFor="smoker" className="text-sm text-foreground/90 cursor-pointer">
                Im Haushalt wird geraucht
              </Label>
            </div>
          </Group>

          {/* Section: Beruf */}
          <Group title="Beruf & Einkommen">
            <Field label="Berufstätigkeit *">
              <UnderlineInput data-testid="bewerbung-occupation" value={form.occupation} onChange={(e) => set("occupation", e.target.value)} placeholder="z. B. Krankenpflegerin, Lehrer, Student" required />
            </Field>
            <Field label="Arbeitgeber">
              <UnderlineInput data-testid="bewerbung-employer" value={form.employer} onChange={(e) => set("employer", e.target.value)} placeholder="optional" />
            </Field>
            <Field label="Monatliches Nettoeinkommen (Haushalt) *">
              <UnderlineSelect testId="bewerbung-income" value={form.income_range} onChange={(v) => set("income_range", v)} placeholder="Bitte wählen"
                options={[
                  ["unter-1500", "Unter 1.500 €"],
                  ["1500-2500", "1.500 – 2.500 €"],
                  ["2500-3500", "2.500 – 3.500 €"],
                  ["3500-5000", "3.500 – 5.000 €"],
                  ["ueber-5000", "Über 5.000 €"],
                ]}
              />
            </Field>
            <div className="flex items-center gap-3 pt-1">
              <Checkbox
                id="schufa"
                data-testid="bewerbung-schufa"
                checked={form.schufa_ready}
                onCheckedChange={(v) => set("schufa_ready", !!v)}
                className="rounded-none border-foreground/40 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <Label htmlFor="schufa" className="text-sm text-foreground/90 cursor-pointer">
                Ich kann eine aktuelle SCHUFA-Auskunft vorlegen
              </Label>
            </div>
          </Group>

          {/* Section: Wunsch */}
          <Group title="Wunschobjekt">
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Wunschstadt">
                <UnderlineSelect testId="bewerbung-city" value={form.preferred_city} onChange={(v) => set("preferred_city", v)} placeholder="Egal / keine Präferenz"
                  options={[
                    ["muenchberg", "Münchberg"],
                    ["selb", "Selb"],
                    ["rehau", "Rehau"],
                    ["leupoldsgruen", "Leupoldsgrün"],
                    ["hof", "Hof"],
                    ["naila", "Naila"],
                    ["egal", "Egal"],
                  ]}
                />
              </Field>
              <Field label="Anzahl Zimmer">
                <UnderlineSelect testId="bewerbung-rooms" value={form.rooms} onChange={(v) => set("rooms", v)} placeholder="keine Präferenz"
                  options={[
                    ["1", "1 Zimmer"],
                    ["2", "2 Zimmer"],
                    ["3", "3 Zimmer"],
                    ["4", "4 Zimmer"],
                    ["5+", "5 oder mehr"],
                  ]}
                />
              </Field>
            </div>
            {properties.length > 0 && (
              <Field label="Konkretes Objekt von Interesse">
                <UnderlineSelect testId="bewerbung-property" value={form.preferred_property} onChange={(v) => set("preferred_property", v)} placeholder="optional"
                  options={[["alle", "Allgemeine Bewerbung"], ...properties.map((p) => [p.id, `${p.title} · ${p.location}`])]}
                />
              </Field>
            )}
            <Field label="Frühester Einzugstermin">
              <UnderlineInput data-testid="bewerbung-move-in" value={form.move_in} onChange={(e) => set("move_in", e.target.value)} placeholder="z. B. ab März 2026 oder flexibel" />
            </Field>
          </Group>

          {/* Section: Message */}
          <Group title="Ihre Nachricht">
            <Field label="Etwas, das wir über Sie wissen sollten">
              <Textarea
                data-testid="bewerbung-message"
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                rows={5}
                placeholder="Stellen Sie sich kurz vor — was ist Ihnen beim Wohnen wichtig?"
                className="rounded-none bg-transparent border border-border focus-visible:ring-0 focus-visible:border-primary text-base"
              />
            </Field>
          </Group>

          <div className="flex items-start gap-3">
            <Checkbox
              id="consent-bewerbung"
              data-testid="bewerbung-consent"
              checked={form.consent}
              onCheckedChange={(v) => set("consent", !!v)}
              className="rounded-none border-foreground/40 data-[state=checked]:bg-primary data-[state=checked]:border-primary mt-1"
            />
            <Label htmlFor="consent-bewerbung" className="text-sm leading-relaxed text-muted-foreground cursor-pointer">
              Ich stimme zu, dass meine Angaben zur Bearbeitung meiner Bewerbung
              gespeichert werden und Keppel Immobilien sie für die Interessentenliste
              verwendet. Weitere Informationen entnehmen Sie unserer{" "}
              <a href="/datenschutz" className="text-primary underline underline-offset-4">Datenschutzerklärung</a>.
            </Label>
          </div>

          <Button
            type="submit"
            data-testid="bewerbung-submit"
            disabled={submitting}
            className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-10 text-sm tracking-wide w-full sm:w-auto"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Bewerbung absenden
          </Button>
        </form>
      </section>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="space-y-6">
      <div className="text-xs uppercase tracking-[0.25em] text-primary border-b border-border pb-3 flex items-center gap-3">
        <span className="inline-block w-2 h-2 bg-accent" />
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function UnderlineInput(props) {
  return (
    <Input
      {...props}
      className="rounded-none bg-transparent border-x-0 border-t-0 border-b border-border h-12 px-0 focus-visible:ring-0 focus-visible:border-primary text-base"
    />
  );
}

function UnderlineSelect({ testId, value, onChange, placeholder, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testId}
        className="rounded-none bg-transparent border-x-0 border-t-0 border-b border-border h-12 px-0 focus:ring-0 text-base"
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="rounded-none">
        {options.map(([v, l]) => (
          <SelectItem key={v} value={v}>{l}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
